import type { LinksFunction, LoaderFunction, MetaFunction } from '@remix-run/node';
import { json, redirect } from '@remix-run/node';
import type { ClientLoaderFunctionArgs, ShouldRevalidateFunction } from '@remix-run/react';
import * as Sentry from '@sentry/remix';

import Document from '~/components/Document';
import remixI18n from '~/i18next.server';
import { loaderSession } from '~/session';
import isPremiumPurchasable from '~/utils/isPremiumPurchasable';

import getWebTheme from './services/api/web_theme/getWebTheme.server';

import App from './components/App';
import { getAppGlobals } from './components/AppGlobals/utils';
import ErrorBoundary from './components/ErrorBoundary';
import { isCommunityInstallPath } from './components/PWA/pwaPromptConfig';

import { buildCommunityLoginPath } from './utils/buildPostAuthRedirectUrl';
import exchangeSubscriptionToken from './utils/exchangeSubscriptionToken';
import { fontPreconnectLinks } from './utils/fontPreconnectLinks';
import getCookieValue from './utils/getCookieValue';
import getForwardedOrigin from './utils/getForwardedOrigin';
import { handleRedirect } from './utils/handleRedirect';
import isSubscriberAccountPage from './utils/isSubscriberAccountPage';
import { readMasqueradeCookie } from './utils/masqueradeCookie';
import previewVersionId from './utils/previewVersionId';
import redirectWithToast from './utils/redirectWithToast/index.server';
import resolveMagicLinkSignIn from './utils/resolveMagicLinkSignIn';
import { setCookieToken } from './utils/setTokenCookie/setCookieToken';
import { shouldRevalidateLoaderData } from './utils/shouldRevalidateLoaderData';

import type { Admin } from './interfaces/admin';
import type { Publication } from './interfaces/publication';
import type { Site } from './interfaces/site';
import type { Subscription } from './interfaces/subscriptions';

import { env } from './env';
import type { ScriptRegistration } from './global';

declare global {
  interface Window {
    beehiiv_registerScript: (script: string) => void;
    beehiiv_scriptsAfterHydration: Map<string /* url */, Map<string /* id/hash */, ScriptRegistration>>;
  }
}

const appGlobals = getAppGlobals();

export const links: LinksFunction = () => fontPreconnectLinks;

export const handle = {
  // In the handle export, we could add a i18n key with namespaces our route
  // will need to load. This key can be a single string or an array of strings.
  i18n: ['common'],
};

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const publication = data?.site?.publication;
  const site = data?.site;

  if (!publication || !site) {
    return [
      { title: 'beehiiv' },
      { description: 'The newsletter platform built for growth.' },
      { keywords: 'Newsletter, Email, Growth, Platform, SaaS, Creator' },
    ];
  }

  const { tags, email_sender_name, logo, rss_feeds } = publication;
  const keywords = tags.map((tag: { name: string }) => tag.name).join(', ');

  const settingsMetadata = site?.viewable_site_version?.metadata;

  const metaData: Array<Record<string, string>> = [
    { title: settingsMetadata?.meta_title || publication.name || '' },
    { description: settingsMetadata?.meta_description || publication.description || '' },
    { keywords },
    { author: email_sender_name || '' },
    {
      tagName: 'link',
      rel: 'icon',
      href: logo?.thumb?.url || '',
      as: 'image',
    },
  ];

  // Icon links live in meta (not in <Head/>) so routes/community.tsx can swap their href for the community logo
  if (logo?.thumb?.url) {
    metaData.push({
      tagName: 'link',
      rel: 'apple-touch-icon',
      href: logo.thumb.url,
    });
  }

  if (publication.thumbnail?.url) {
    metaData.push({
      tagName: 'link',
      rel: 'apple-touch-startup-image',
      href: publication.thumbnail.url,
    });
  }

  if (settingsMetadata?.noindex_enabled || publication.private) {
    metaData.push({ robots: 'noindex' });
  }

  if (rss_feeds?.length) {
    metaData.push({
      tagName: 'link',
      rel: 'alternate',
      type: 'application/rss+xml',
      title: `${publication.name} RSS Feed`,
      href: rss_feeds[0].url || '',
    });
  }

  return metaData;
};

export interface RootLoaderData {
  site: Site;
  recommendingPublication: Pick<Publication, 'id' | 'name'>;
  rootStyles: string;
  isLoggedIn: boolean;
  isFetchingSubscription: boolean;
  subscription: Subscription;
  admin: Admin;
  isProdEnv: boolean;
  facebookAppId: string;
  currentUrl: string;
  showLoggedInToast: boolean;
  isStagingPreview: boolean;
  isMasquerading: boolean;
  masqueradeEmail: string;
  SENTRY_RELEASE: string;
  isPostsPage: boolean;
  noScript: boolean;
  orderStatusPollMaxAttempts: number | null;
}

export const loader: LoaderFunction = async ({ request, context }) => {
  const { session } = await loaderSession(request);
  const url = new URL(request.url);
  const searchParams = url.searchParams;

  // If the request has cache buster, remove it and redirect without cache
  if (searchParams.has('__cb')) {
    searchParams.delete('__cb');
    throw redirect(`${url.pathname}${url.search}`, {
      headers: { 'cache-control': 'no-store' },
    });
  }
  const noScript = searchParams.get('no_script') === 'true';

  const [site, webTheme] = await Promise.all([context.getSite(request, session), getWebTheme(request, session)]);
  const { publication } = site;

  if (site.site_redirects_enabled) {
    handleRedirect(request, site.site_redirects || []);
  }

  Sentry.setContext('publication', {
    id: publication.id,
    name: publication.name,
  });

  const locale = publication?.language || 'en';
  const t = await remixI18n.getFixedT(locale, 'common');

  if (url.searchParams.get('upgrade') === 'success') {
    return redirect(
      redirectWithToast({
        baseUrl: '/',
        toast: 'success',
        title: t('upgrade.success.title') || '',
        message: t('upgrade.success.message'),
      }),
    );
  }

  // Check if the request host is different than the custom domain configured
  //   for the current publication (if any). If it is, we will redirect to the
  //   custom domain, retaining any parameters. This is useful for redirecting
  //   beehiiv publication subdomains to custom domains.
  //
  //   Example: get mypublication.beehiiv.com/p/test?foo=bar
  //     => 301 https://blog.mypublication.com/p/test?foo=bar
  // Make sure to strip the port from the host name if present
  const hostName = (request.headers.get('x-forwarded-host') || url.hostname).split(':')[0];

  const configuredDomain = publication.configured_domain;
  if (configuredDomain && configuredDomain !== hostName) {
    url.protocol = 'https:'; // Ensure the protocol is https
    url.hostname = configuredDomain;
    throw redirect(url.href, {
      status: 301,
    });
  }
  const cookieHeader = request.headers.get('Cookie');
  const isStagingPreview = Boolean(previewVersionId(request));
  const { isMasquerading, email: masqueradeEmail } = readMasqueradeCookie(cookieHeader);

  // Subscribe always exists. Home does not until the first post is published,
  // and that empty-home redirect drops this toast.
  const isUpgradePage = url.pathname?.startsWith('/upgrade');
  if (!isPremiumPurchasable(publication) && isUpgradePage) {
    return redirect(
      redirectWithToast({
        baseUrl: '/subscribe',
        toast: 'error',
        message: t('upgrade.error.unavailable'),
      }),
      307,
    );
  }

  return json({
    site,
    isLoggedIn: false,
    isFetchingSubscription: true,
    rootStyles: webTheme?.root_styles,
    admin: null,
    subscription: null,
    isProdEnv: process.env.NODE_ENV === 'production',
    facebookAppId: env.FACEBOOK_APP_ID,
    // Server-rendered entry URL on the public origin. The root loader does not
    // revalidate on client navigation, so read the live path from useLocation.
    currentUrl: new URL(url.pathname, getForwardedOrigin(request)).toString(),
    showLoggedInToast: false,
    hostName,
    isStagingPreview,
    isMasquerading,
    masqueradeEmail,
    // include latest release in root to compare with initial document request
    // in AppGlobals. If different, for a refresh
    SENTRY_RELEASE: appGlobals.SENTRY_RELEASE,
    noScript,
    orderStatusPollMaxAttempts: env.ORDER_STATUS_POLL_MAX_ATTEMPTS || null,
  });
};

export const clientLoader = async ({ request, serverLoader }: ClientLoaderFunctionArgs) => {
  const serverData = await serverLoader<RootLoaderData>();
  if (!serverData?.site?.publication) {
    // show the refresh page message if we didn't get server data
    throw new Error('RefreshPageError');
  }
  try {
    const url = new URL(request.url);
    const publication = serverData.site.publication;

    const hasToastParams = url.searchParams.get('toast') && url.searchParams.get('message');
    const jwtToken = url.searchParams.get('jwt_token');
    const cookieToken = getCookieValue('token', document.cookie);
    const showLoggedInToast = jwtToken && !hasToastParams;
    const token = jwtToken || cookieToken;
    const adminToken = url.searchParams.get('admin_token') || getCookieValue('admin_token', document.cookie);
    const requests = [];

    if (token) {
      requests.push(exchangeSubscriptionToken(token, serverData.isProdEnv));
    } else {
      requests.push(Promise.resolve(null));
    }

    if (adminToken) {
      requests.push(
        fetch('/current_admin', {
          method: 'POST',
          headers: {
            'Sec-Fetch-Dest': 'empty',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ admin_token: adminToken }),
        }).then((res) => {
          if (res.ok) {
            document.cookie = setCookieToken({
              key: 'admin_token',
              token: adminToken,
              isProdEnv: serverData?.isProdEnv,
            });
          }
          return res;
        }),
      );
    } else {
      requests.push(Promise.resolve(null));
    }

    let [subscriptionRes, adminRes] = await Promise.all(requests);

    if (jwtToken && isCommunityInstallPath(url.pathname)) {
      const signInRes = await resolveMagicLinkSignIn({
        magicLinkResponse: subscriptionRes,
        jwtToken,
        cookieToken,
        exchangeToken: (fallbackToken) => exchangeSubscriptionToken(fallbackToken, serverData.isProdEnv),
      });
      if (!signInRes) {
        window.location.replace(buildCommunityLoginPath(url));
        return serverData;
      }
      subscriptionRes = signInRes;
    }

    const [subscription, admin] = await Promise.all([
      subscriptionRes?.ok ? subscriptionRes.json() : null,
      adminRes?.ok ? adminRes.json() : null,
    ]);

    // PrivatePubs redirect: private pubs block all non-home pages,
    // approval-required pubs only block post pages (matching V1 behavior)
    const isPrivate = serverData.site?.publication?.private;
    const requireSubscriberApproval = serverData.site?.publication?.require_subscriber_approval;
    const { isStagingPreview } = serverData;
    const isHomePage = url.pathname === '/';
    const isPostsPage = url.pathname?.startsWith('/p/');
    const isDraftPost = url.searchParams.get('draft') === 'true';
    const isAccountPage = isSubscriberAccountPage(url.pathname);

    if (!subscription && !isStagingPreview && isPrivate && !isHomePage && !isDraftPost && !isAccountPage) {
      window.location.href = '/';
      return serverData;
    }

    if (!subscription && !isStagingPreview && requireSubscriberApproval && isPostsPage && !isDraftPost) {
      window.location.href = '/';
      return serverData;
    }

    // Login page redirect: This specific redirect is for the /login route and checks if the user is already logged in
    const isUserLoggedIn = Boolean(subscription);
    const isLoginPage = url.pathname?.startsWith('/login');
    if (isUserLoggedIn && isLoginPage) {
      window.location.href = '/';
      return serverData;
    }

    if (subscription) {
      Sentry.setContext('subscription', {
        id: subscription.id,
        tier: subscription.tier,
        status: subscription.status,
        email: subscription.email,
        subscriberId: subscription.subscriber_id,
      });
    }

    // Strip only the security-sensitive tokens. Toast params (toast/message/title/toastId)
    // are owned by useNotifications, which clears them via a router-aware setSearchParams;
    // deleting them here with a raw replaceState races that consumer and swallows the toast.
    if (url.searchParams.has('jwt_token') || url.searchParams.has('admin_token')) {
      url.searchParams.delete('jwt_token');
      url.searchParams.delete('admin_token');
      window.history.replaceState(window.history.state, document.title, url.pathname + url.search);
    }

    // If the publication is suspended, but a subscription is found
    // redirect to the manage page (unless already there)
    const managePagePattern = /^\/subscribe\/[^/]+\/manage$/;
    const isOnManagePage = managePagePattern.test(url.pathname);
    if (publication.website_suspended && subscription && !isOnManagePage) {
      window.location.href = `/subscribe/${subscription.subscriber_id}/manage`;
    }

    return {
      ...serverData,
      isLoggedIn: !!subscription,
      admin: admin ?? null,
      isFetchingSubscription: false,
      subscription,
      showLoggedInToast: !!(showLoggedInToast && subscription),
      isPostsPage,
    };
  } catch (_error) {
    Sentry.captureException(_error);

    return {
      ...serverData,
      isLoggedIn: false,
      admin: null,
      subscription: null,
      isFetchingSubscription: false,
    };
  }
};

clientLoader.hydrate = true;

// Use Layout export to wrap both App and ErrorBoundary in Document
// https://v2.remix.run/docs/file-conventions/root#layout-export
export const Layout = ({ children }: { children: React.ReactNode }) => {
  return <Document>{children}</Document>;
};

const SentryApp = Sentry.withSentry(App);
// biome-ignore lint/style/useComponentExportOnlyModules: Sentry wrapped component export
export default SentryApp;

// Export default ErrorBoundary for Remix
// biome-ignore lint/style/useComponentExportOnlyModules: ErrorBoundary export for Remix
export { ErrorBoundary };

export const shouldRevalidate: ShouldRevalidateFunction = shouldRevalidateLoaderData;
